import { backend } from "@/integrations/backend/client";

export interface SyncedGameProgress {
  user_id: string;
  total_score: number;
  islands_completed: number;
  badges_awarded: number;
}

interface BadgeRecord {
  id: string;
  slug: string;
}

interface IslandRecord {
  id: string;
  slug: string;
  order_index?: number | null;
  is_active?: boolean | null;
  crew_id?: string | null;
}

interface StationRecord {
  id: string;
  island_id: string;
  slug: string;
  source_station_id?: string | null;
}

interface StationProgressRecord {
  station_id: string;
  station_points?: number | null;
  completed_at?: string | null;
}

interface IslandProgressRecord {
  island_id: string;
  score?: number | null;
  completed_at?: string | null;
}

export interface UserBadgeRecord {
  badge_id: string;
  earned_at: string;
}

const REQUIRED_STATION_SOURCE_IDS_BY_ISLAND: Record<string, string[]> = {
  "welcome-cove": ["station_1"],
  "welcome-cove-ii": ["station_2"],
  "prompt-peninsula": ["station_3"],
  "automation-atoll": ["station_4"],
  "ethics-isle": ["station_5"],
  "collaboration-coral": ["station_6"],
};

function baseIslandSlug(slug: string) {
  return slug.replace(/-[0-9a-f]{8}$/i, "");
}

function normalizedStationKey(value: string | null | undefined) {
  return (value ?? "").trim().toLowerCase().replace(/-/g, "_");
}

function safePoints(value: number | null | undefined) {
  const points = Number(value ?? 0);
  return Number.isFinite(points) ? Math.max(0, Math.trunc(points)) : 0;
}

async function saveCompletedIslandProgress(options: {
  userId: string;
  islandId: string;
  score: number;
  completedAt: string;
  hasExistingProgress: boolean;
}) {
  const payload = {
    score: safePoints(options.score),
    completed_at: options.completedAt,
  };

  if (options.hasExistingProgress) {
    const { error } = await backend
      .from("island_progress")
      .update(payload)
      .eq("user_id", options.userId)
      .eq("island_id", options.islandId);
    if (error) throw error;
    return;
  }

  const { error: insertError } = await backend.from("island_progress").insert({
    user_id: options.userId,
    island_id: options.islandId,
    ...payload,
  });

  if (!insertError) return;

  const { error: updateError } = await backend
    .from("island_progress")
    .update(payload)
    .eq("user_id", options.userId)
    .eq("island_id", options.islandId);
  if (updateError) throw updateError;
}

export async function syncCurrentUserGameProgress() {
  try {
    const { data, error } = await backend.rpc("sync_user_game_progress");
    if (error) throw error;

    const row = (Array.isArray(data) ? data[0] : data) as
      | Partial<SyncedGameProgress>
      | null
      | undefined;

    return row
      ? {
          user_id: String(row.user_id ?? ""),
          total_score: Number(row.total_score ?? 0),
          islands_completed: Number(row.islands_completed ?? 0),
          badges_awarded: Number(row.badges_awarded ?? 0),
        }
      : null;
  } catch (error) {
    console.warn("Could not sync game progress:", error);
    return null;
  }
}

export async function repairCurrentUserProgressAndBadges(userId: string) {
  await syncCurrentUserGameProgress();

  try {
    const [
      badgesResponse,
      islandsResponse,
      stationsResponse,
      stationProgressResponse,
      islandProgressResponse,
      crewsResponse,
    ] = await Promise.all([
      backend.from("badges").select("id, slug"),
      backend.from("islands").select("id, slug, order_index, is_active, crew_id"),
      backend.from("stations").select("id, island_id, slug, source_station_id"),
      backend
        .from("station_progress")
        .select("station_id, station_points, completed_at")
        .eq("user_id", userId),
      backend
        .from("island_progress")
        .select("island_id, score, completed_at")
        .eq("user_id", userId),
      backend.from("crews").select("id").eq("owner_id", userId).limit(1),
    ]);

    if (badgesResponse.error) throw badgesResponse.error;
    if (islandsResponse.error) throw islandsResponse.error;
    if (stationsResponse.error) throw stationsResponse.error;
    if (stationProgressResponse.error) throw stationProgressResponse.error;
    if (islandProgressResponse.error) throw islandProgressResponse.error;

    const badges = (badgesResponse.data || []) as BadgeRecord[];
    const islands = (islandsResponse.data || []) as IslandRecord[];
    const stations = (stationsResponse.data || []) as StationRecord[];
    const stationProgress = (stationProgressResponse.data ||
      []) as StationProgressRecord[];
    const islandProgress = (islandProgressResponse.data ||
      []) as IslandProgressRecord[];
    const badgeBySlug = new Map(badges.map((badge) => [badge.slug, badge]));
    const progressByStationId = new Map(
      stationProgress.map((progress) => [progress.station_id, progress]),
    );
    const completedIslandProgress = new Map(
      islandProgress
        .filter((progress) => Boolean(progress.completed_at))
        .map((progress) => [progress.island_id, progress]),
    );

    const completedIslandIds = new Set(completedIslandProgress.keys());
    let repairedIslandProgress = false;

    for (const island of islands.filter(
      (item) => item.is_active !== false && !item.crew_id,
    )) {
      const requiredSourceIds =
        REQUIRED_STATION_SOURCE_IDS_BY_ISLAND[baseIslandSlug(island.slug)];
      const islandStations = stations.filter((station) => {
        if (station.island_id !== island.id) return false;
        if (!requiredSourceIds) return true;

        const sourceKey = normalizedStationKey(station.source_station_id);
        const slugKey = normalizedStationKey(station.slug);
        return (
          requiredSourceIds.includes(sourceKey) ||
          requiredSourceIds.includes(slugKey)
        );
      });

      if (islandStations.length === 0) continue;

      const stationRows = islandStations.map((station) =>
        progressByStationId.get(station.id),
      );
      const isComplete = stationRows.every((progress) =>
        Boolean(progress?.completed_at),
      );
      if (!isComplete) continue;

      const score = stationRows.reduce(
        (sum, progress) => sum + safePoints(progress?.station_points),
        0,
      );
      const completedAt =
        stationRows
          .map((progress) => progress?.completed_at)
          .filter(Boolean)
          .sort()
          .at(-1) ?? new Date().toISOString();

      const existingProgress = completedIslandProgress.get(island.id);
      if (!existingProgress || safePoints(existingProgress.score) < score) {
        await saveCompletedIslandProgress({
          userId,
          islandId: island.id,
          score,
          completedAt: existingProgress?.completed_at ?? completedAt,
          hasExistingProgress: Boolean(existingProgress),
        });
        repairedIslandProgress = true;
      }

      completedIslandIds.add(island.id);
      completedIslandProgress.set(island.id, {
        island_id: island.id,
        score,
        completed_at: completedAt,
      });
    }

    const completedIslands = islands.filter((island) =>
      completedIslandIds.has(island.id),
    );
    const completedBaseSlugs = new Set(
      completedIslands.map((island) => baseIslandSlug(island.slug)),
    );
    const earnedSlugs = new Set<string>();

    if (completedIslandIds.size > 0) earnedSlugs.add("island-explorer");
    if (completedBaseSlugs.has("prompt-peninsula"))
      earnedSlugs.add("prompt-master");
    if (completedBaseSlugs.has("automation-atoll"))
      earnedSlugs.add("automation-ace");
    if (completedBaseSlugs.has("ethics-isle")) earnedSlugs.add("ethics-expert");
    if (completedBaseSlugs.has("collaboration-coral"))
      earnedSlugs.add("team-player");
    if ((crewsResponse.data || []).length > 0) earnedSlugs.add("crew-captain");

    const activePersonalIslandCount = islands.filter(
      (island) => island.is_active !== false && !island.crew_id,
    ).length;
    if (
      activePersonalIslandCount > 0 &&
      completedIslands.filter((island) => !island.crew_id).length >=
        activePersonalIslandCount
    ) {
      earnedSlugs.add("ai-ambassador");
    }

    const earnedBadgeRows = [...earnedSlugs]
      .map((slug) => badgeBySlug.get(slug))
      .filter(Boolean)
      .map((badge) => ({
        user_id: userId,
        badge_id: badge!.id,
        earned_at: new Date().toISOString(),
      }));

    if (earnedBadgeRows.length > 0) {
      const { error } = await backend
        .from("user_badges")
        .upsert(earnedBadgeRows, { onConflict: "user_id,badge_id" });
      if (error) throw error;
    }

    if (repairedIslandProgress) {
      await syncCurrentUserGameProgress();
    }
  } catch (error) {
    console.warn("Could not repair game badges from progress:", error);
  }

  const { data, error } = await backend
    .from("user_badges")
    .select("badge_id, earned_at")
    .eq("user_id", userId);
  if (error) {
    console.warn("Could not reload user badges after repair:", error);
    return [];
  }

  return (data || []) as UserBadgeRecord[];
}
